import { CoinNetworksResponse, useGetCoinNetworks } from '@/api';
import { CryptoCurrency } from '@/api/common.types';
import { ChainsDataStore, useChainsData } from '@/store/chainsData';
import { walletConnectSupportList } from '@/utils/constants';
import { IsERC20Network, isEVMNetwork } from '@/utils/functions';
import { useMemo } from 'react';
import { shallow } from 'zustand/shallow';

function convertToHex(chainId: String | null | undefined) {
  if (typeof chainId === 'string') {
    const splitChainId = chainId.split(':');
    if (splitChainId[0].includes('eip155')) {
      const hexChainId = Number(splitChainId?.[1]).toString(16);
      return `0x${hexChainId}`;
    }
  }
  return chainId;
}

interface IConvertedFiatToCryptoData {
  cryptoCurrency: CryptoCurrency;
  onSuccess?: (data: CoinNetworksResponse[]) => void;
  enabled?: boolean;
  isConnectedWalletOnly?: boolean;
}

const formatChainsFromResponse = (data: CoinNetworksResponse[]) => {
  // Filter out chains that are not supported by wallet connect and are not eip155 (ethereum chains)
  return data
    .filter(
      (item) =>
        !!item?.chainId &&
        walletConnectSupportList.includes(item?.cryptoNetwork) &&
        item?.chainId?.startsWith('eip155'),
    )
    .map((item) => ({
      id: convertToHex(item?.chainId),
      token: item?.cryptoNetwork,
      label: item?.description,
      rpcUrl: item?.rpcUrl,
    })) as ChainsDataStore['chainsData'];
};

export const useGetCryptoNetworkData = ({
  cryptoCurrency,
  onSuccess,
  enabled,
  isConnectedWalletOnly = false,
}: IConvertedFiatToCryptoData) => {
  const { data, isLoading, isFetching, error, refetch } = useGetCoinNetworks([
    { cryptoCurrency },
    {
      onSuccess: (data) => {
        setChainsData({ chainsData: formatChainsFromResponse(data) });
        if (onSuccess) {
          onSuccess(data);
        }
      },
      enabled,
    },
  ]);

  const [chains, setChainsData] = useChainsData((state) => [state.chainsData, state.setData], shallow);

  const sortedData = useMemo(() => {
    if (isConnectedWalletOnly) {
      return data?.filter(
        (item) =>
          IsERC20Network(item?.cryptoNetwork as CryptoCurrency) &&
          isEVMNetwork(chains, item?.cryptoNetwork as CryptoCurrency),
      );
    }

    return data?.sort((a, b) => {
      if (a?.cryptoNetwork === 'ETH') return -1; // Ensure ETH is always first
      if (b?.cryptoNetwork === 'ETH') return 1; // Ensure ETH is always first
      return 0; // Keep original order for other cases
    });
  }, [chains, data, isConnectedWalletOnly]);

  return {
    data: sortedData ?? [],
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
